import React, { useEffect, useRef, useState } from "react";
import "./LandingPage.css";
import { ArrowRight, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { meetingTypes } from "@teleprompt/shared/config/meetingOptions";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import practiceImage from '../../assets/images/landingPage/practice.jpg';
import feedbackImage from '../../assets/images/landingPage/feedback.jpg';
import trainingImage from '../../assets/images/landingPage/Training.jpg';
import landingPageVideo from '../../assets/videos/landingPage/landingPageVid.mp4';
import { closeDemoModal, selectDemoModalOpen } from "../../store/uiSlice";
import demoVideo from '../../assets/demo/demo-telome.MOV';
import appStoreBadge from '../../assets/logo/appstore/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg';
import playStoreBadge from '../../assets/logo/playstore/GetItOnGooglePlay_Badge_Web_color_English.svg';
import ContinueInAppPrompt from "./components/ContinueInAppPrompt/ContinueInAppPrompt";
import MicLevelVisualizer from '../../components/MicLevelVisualizer/MicLevelVisualizer';
import brainIcon from '../../assets/icons/conversation-starter-icons/icons8-brain.svg';
import briefcaseIcon from '../../assets/icons/conversation-starter-icons/icons8-briefcase.svg';
import coffeeIcon from '../../assets/icons/conversation-starter-icons/icons8-coffee.svg';
import conferenceIcon from '../../assets/icons/conversation-starter-icons/icons8-conference-foreground-selected.svg';
import earthIcon from '../../assets/icons/conversation-starter-icons/icons8-earth.svg';
import languageIcon from '../../assets/icons/conversation-starter-icons/icons8-language.svg';
import magicIcon from '../../assets/icons/conversation-starter-icons/icons8-magic.svg';
import podiumIcon from '../../assets/icons/conversation-starter-icons/icons8-podium.svg';
import puzzleIcon from '../../assets/icons/conversation-starter-icons/icons8-puzzle.svg';
import salesIcon from '../../assets/icons/conversation-starter-icons/icons8-sales.svg';
import supportIcon from '../../assets/icons/conversation-starter-icons/icons8-support.svg';
import templateIcon from '../../assets/icons/conversation-starter-icons/icons8-template.svg';

const conversationIconByType = {
  custom: magicIcon,
  'job-interview': briefcaseIcon,
  'small-talk': coffeeIcon,
  'sales-call': salesIcon,
  'performance-review': brainIcon,
  'client-presentation': templateIcon,
  'team-meeting': conferenceIcon,
  networking: earthIcon,
  'ielts-speaking-interview': languageIcon,
  'customer-support': supportIcon,
  recruiting: puzzleIcon,
  debate: podiumIcon,
};

const practiceBenefits = [
  {
    title: 'Walk in prepared',
    body: 'Rehearse interviews, presentations, and difficult conversations before they happen.',
  },
  {
    title: 'Practice without pressure',
    body: 'Repeat a scenario, try a different approach, and build confidence at your own pace.',
  },
  {
    title: 'Know what to improve',
    body: 'Turn each conversation into focused feedback and a practical training plan.',
  },
  {
    title: 'Make progress visible',
    body: 'Build streaks, revisit training sessions, and turn regular practice into steady improvement.',
  },
];

const practiceBenefitPages = Array.from(
  { length: Math.ceil(practiceBenefits.length / 2) },
  (_, index) => practiceBenefits.slice(index * 2, index * 2 + 2)
);

function ConversationBadgeRail({ hidden = false }) {
  return (
    <div className="use-badge-group" aria-hidden={hidden || undefined}>
      {meetingTypes.map((type) => (
        <div className="use-item" key={type.value}>
          <img
            alt=""
            aria-hidden="true"
            className="use-icon"
            src={conversationIconByType[type.value] || magicIcon}
          />
          <span>{type.label}</span>
        </div>
      ))}
    </div>
  );
}

function PracticeBenefitsCarousel() {
  const [page, setPage] = useState(0);
  const lastPage = practiceBenefitPages.length - 1;

  return (
    <div className="benefit-carousel">
      <div className="benefit-carousel-viewport">
        <div
          className="benefit-carousel-track"
          style={{ transform: `translateX(-${page * 100}%)` }}
        >
          {practiceBenefitPages.map((benefits, pageIndex) => (
            <div
              aria-hidden={pageIndex !== page}
              className="social-proof-page"
              key={benefits.map((benefit) => benefit.title).join('-')}
            >
              {benefits.map((benefit) => (
                <article className="social-proof-card" key={benefit.title}>
                  <h3>{benefit.title}</h3>
                  <p>{benefit.body}</p>
                </article>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="benefit-carousel-controls">
        <button
          aria-label="Previous benefits"
          className="benefit-carousel-button"
          disabled={page === 0}
          onClick={() => setPage((current) => Math.max(0, current - 1))}
          type="button"
        >
          <ChevronLeft aria-hidden="true" size={22} />
        </button>
        <button
          aria-label="Next benefits"
          className="benefit-carousel-button"
          disabled={page === lastPage}
          onClick={() => setPage((current) => Math.min(lastPage, current + 1))}
          type="button"
        >
          <ChevronRight aria-hidden="true" size={22} />
        </button>
      </div>
    </div>
  );
}

function RealConversationSection({ onGetStarted }) {
  const sectionRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    const video = videoRef.current;
    if (!section || !video || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          void video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.35 }
    );

    observer.observe(section);
    return () => {
      observer.disconnect();
      video.pause();
    };
  }, []);

  return (
    <section className="real-conversations" ref={sectionRef}>
      <div className="real-conversations-inner">
        <div className="real-conversations-copy">
          <p className="real-conversations-eyebrow">Practice for real life</p>
          <h2>
            Be ready for job interviews.<br />
            Make small talk matter.<br />
            <span>Be heard at work.</span>
          </h2>
          <p className="real-conversations-description">
            Rehearse the conversations that matter, then walk into the real moment with confidence.
          </p>
          <button className="hero-button" onClick={onGetStarted} type="button">
            Start Practicing <ArrowRight className="icon-inline" />
          </button>
        </div>

        <div className="real-conversations-video-frame">
          <video
            aria-hidden="true"
            className="real-conversations-video"
            disablePictureInPicture
            loop
            muted
            playsInline
            preload="metadata"
            ref={videoRef}
            src={landingPageVideo}
            tabIndex={-1}
          />
        </div>
      </div>
    </section>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isDemoModalOpen = useSelector(selectDemoModalOpen); 


  return (
    <div className="landing-container">
      {/* ===== HERO SECTION ===== */}
      <section className="hero">
        <div className="hero-content">
          <h1 className="hero-title">Practice any conversation, anytime with AI.</h1>
          <p className="hero-subtitle">
            Select a conversation type or create your own. Practice with AI and get instant
            feedback and personalized training sessions to reach your goals.
          </p>
          <button
            className="hero-button"
            onClick={() => navigate("/auth")}
          >
            Get Started Free <ArrowRight className="icon-inline" />
          </button>
          <p className="hero-appstore-title"><strong>Get the App</strong></p>
          <div className="hero-appstore-badges">
            <a
              className="hero-appstore-link"
              href="https://apps.apple.com/us/app/telome/id6761741102"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download Telome on the App Store"
            >
              <img
                className="hero-appstore-badge hero-appstore-badge--apple"
                src={appStoreBadge}
                alt="Download on the App Store"
              />
            </a>
            <a
              className="hero-appstore-link"
              href="https://play.google.com/store/apps/details?id=com.paulkalaitzidis.telome.modbile&pli=1"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download Telome on Google Play"
            >
              <img
                className="hero-appstore-badge hero-appstore-badge--google"
                src={playStoreBadge}
                alt="Get it on Google Play"
              />
            </a>
          </div>
        </div>
      </section>

      {/* ===== USE IT FOR ANYTHING ===== */}
      <section className="use-anything">
        <h2 className="section-title">Use it for anything</h2>
        <div className="use-marquee">
          <div className="use-marquee-track">
            <ConversationBadgeRail />
            <ConversationBadgeRail hidden />
          </div>
        </div>
      </section>

      <RealConversationSection onGetStarted={() => navigate('/auth')} />

      {/* ===== HOW IT WORKS ===== */}
<section className="how-it-works">
  <h2 className="section-title">How it works</h2>
  <div className="how-grid">
    <div className="how-card">
      <div className="how-card-copy">
        <div className="how-practice-visualizer">
          <MicLevelVisualizer demo />
        </div>
        <h3>Practice</h3>
        <p>
          Select or <strong className="highlighted-p">create your own</strong> conversations and practice with AI.
        </p>
      </div>
      <div className="how-product-image-frame">
        <img
          src={practiceImage}
          alt="A practice conversation with an AI recruiter"
          className="how-product-image"
          loading="lazy"
          decoding="async"
          sizes="(max-width: 760px) calc(100vw - 4rem), 34rem"
        />
      </div>
    </div>

    <div className="how-card">
      <div className="how-card-copy">
        <h3>Get Feedback</h3>
        <p>
          Get instant feedback with your strengths, weaknesses, and a <strong className="highlighted-p">plan forward.</strong>
        </p>
      </div>
      <div className="how-product-image-frame">
        <img
          src={feedbackImage}
          alt="Telome feedback showing speaking pace and filler words"
          className="how-product-image"
          loading="lazy"
          decoding="async"
          sizes="(max-width: 760px) calc(100vw - 4rem), 34rem"
        />
      </div>
    </div>

    <div className="how-card">
      <div className="how-card-copy">
        <h3>Improve</h3>
        <p>
          Take personalized training sessions to <strong className="highlighted-p">achieve</strong> your goals.
        </p>
      </div>
      <div className="how-product-image-frame">
        <img
          src={trainingImage}
          alt="A personalized Telome training plan"
          className="how-product-image"
          loading="lazy"
          decoding="async"
          sizes="(max-width: 760px) calc(100vw - 4rem), 34rem"
        />
      </div>
    </div>
  </div>
</section>

      <section className="social-proof" aria-labelledby="social-proof-title">
        <div className="social-proof-inner">
          <div className="social-proof-heading">
            <h2 className="section-title" id="social-proof-title">People love Telome</h2>
            <a
              className="store-rating"
              href="https://apps.apple.com/us/app/telome/id6761741102"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Telome is rated 5.0 on the App Store"
            >
              <span className="store-rating-stars" aria-hidden="true">
                {Array.from({ length: 5 }, (_, index) => (
                  <Star fill="currentColor" key={index} size={18} strokeWidth={2} />
                ))}
              </span>
              <span>5.0 on the App Store</span>
            </a>
          </div>

          <PracticeBenefitsCarousel />
        </div>
      </section>


      {/* ===== FOOTER CTA ===== */}
      <section className="footer-cta">
        <h3>Practice any conversation, on your own schedule.</h3>
        <button
          className="hero-button"
          onClick={() => navigate("/auth")}
        >
          Get Started Free <ArrowRight className="icon-inline" />
        </button>
      </section>

      {/* ======= COMMENTED OUT SECTIONS ======= */}
      {/*
        <section className="features">...</section>
        <section className="enterprise-features">...</section>
        <section className="cta">...</section>
      */}

      {/* ===== DEMO MODAL ===== */}
      {isDemoModalOpen && (
        <div
          className="demo-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Watch demo"
        >
          <div className="demo-modal">
            <button
              className="demo-modal-close"
              aria-label="Close demo"
              onClick={() => dispatch(closeDemoModal())}
            >
              ×
            </button>
            <div className="demo-modal-body">
              <h2 className="demo-modal-title">See Telome in action</h2>
              <p className="demo-modal-subtitle">
                Quick walkthrough of how you can practice any conversation with AI.
              </p>

              {/* Placeholder for video / loom / YouTube embed */}
              <div className="demo-video-container">
  <video
    className="demo-video"
    src={demoVideo}
    controls
    playsInline
  />
</div>

            </div>
          </div>
        </div>
      )}

      <ContinueInAppPrompt />
    </div>
  );
}
